import React, { useMemo } from 'react';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { t } from 'i18next';
import { format } from 'date-fns';
import BarChart from '../../../../../shared/components/chart/bar-chart/BarChart';
import { useAccount } from '../../../../../shared/contexts/account/AccountContexts';
import {
  useEffectiveAddons,
  useEffectiveFeatures,
} from '../../../../../shared/contexts/effective-features/EffectiveFeaturesContext';
import { TagsResponse } from '../../../../../shared/models/tags/TagsResponse';
import { KeywordResponse } from '../../../../../shared/models/monitor/KeywordResponse';
import {
  ALL_PROJECT_CHAT_TYPES,
  ChatType,
  getChatTypeToName,
} from '../../../../../shared/types/monitor/ChatType';
import { canShowModel } from '../../../../../shared/utils/utils';
import SimpleBar from 'simplebar-react';
import useIsMobile from '../../../../../shared/hooks/global/UseIsMobile';

// Canonical chat type → model key (for canShowModel / modelDataRanges lookup).
const CHAT_TYPE_TO_MODEL_KEY: Record<string, string> = {
  GPT: 'GPT',
  GEMINI: 'GEMINI',
  CLAUDE: 'CLAUDE',
  DEEP_SEEK: 'DEEP_SEEK',
  PERPLEXITY: 'PERPLEXITY',
  GROK: 'GROK',
  AI_MODE_BROWSER: 'AI_MODE',
  AI_OVERVIEW_NEW_BROWSER: 'AI_OVERVIEW_NEW',
  COPILOT_BROWSER: 'COPILOT',
};

interface ShareOfVoiceBarChartProps {
  allModelsStatsMatrix: string[][] | null;
  brandColors: { name: string; color: string }[] | null;
  firmName: string;
  keywordsMapper: Map<string, string>;
  commonKeywords: KeywordResponse[];
  tags: TagsResponse[];
  // Real first/last data date per model (model key → {from,to} ISO strings) for archival-model markers.
  modelDataRanges?: Record<string, { from: string; to: string }>;
  /** Opt-in: render a custom legend icon (e.g. brand logo/initial) instead of the colored dot. */
  renderLegendIcon?: (label: string, color: string) => React.ReactNode;
}

const ShareOfVoiceBarChart: React.FC<ShareOfVoiceBarChartProps> = ({
  allModelsStatsMatrix,
  brandColors,
  firmName,
  keywordsMapper,
  commonKeywords,
  tags,
  modelDataRanges,
  renderLegendIcon,
}) => {
  const { accountResponse } = useAccount();
  const effectiveFeatures = useEffectiveFeatures();
  const effectiveAddons = useEffectiveAddons();
  const isMobile = useIsMobile();

  // Mark each archival model's axis label (data present but model no longer on the account) with an
  // "ⓘ" + tooltip of its real data span. Only for NEW_STATS, which surfaces historical models.
  const axisLabelMarkers = useMemo(() => {
    const hasNewStats = effectiveAddons.includes('NEW_STATS');
    if (!hasNewStats) return undefined;
    const features = effectiveFeatures;
    const markers: Record<string, string> = {};
    for (const chatType of ALL_PROJECT_CHAT_TYPES) {
      const modelKey = CHAT_TYPE_TO_MODEL_KEY[chatType];
      if (!modelKey || canShowModel(modelKey, features)) continue;
      const dataRange = modelDataRanges?.[modelKey];
      markers[getChatTypeToName(chatType as ChatType)] =
        dataRange?.from && dataRange?.to
          ? t('historicalModelDisabledRange', {
              from: format(new Date(dataRange.from), 'd MMM yyyy'),
              to: format(new Date(dataRange.to), 'd MMM yyyy'),
            })
          : t('historicalModelDisabled');
    }
    return Object.keys(markers).length > 0 ? markers : undefined;
  }, [accountResponse, modelDataRanges, effectiveFeatures, effectiveAddons]);

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('shareOfVoiceByLlm')}
          <CustomTooltip tooltipText="shareOfVoiceByLlmTooltip" addMargin={false} />
        </div>
      </div>

      <div className="stats-box stats-box--bar-chart">
        <div className="stats-box__content">
          {/* Telefon: ten sam wykres obrócony — chatboty w pionie, marki jako poziome słupki — zamiast
              kolumn na 1200 px przewijanych w poziomie. */}
          {isMobile ? (
            <BarChart
              data={allModelsStatsMatrix}
              colorsByName={brandColors || undefined}
              selectedBrand={firmName}
              keywordsMapper={keywordsMapper}
              commonKeywords={commonKeywords}
              tags={tags}
              valueSuffix="%"
              renderLegendIcon={renderLegendIcon}
              capitalizeBrands
              horizontal
            />
          ) : (
            <SimpleBar className="stats-box__scroll-box" style={{ maxWidth: '100%' }} autoHide={false}>
              <BarChart
                data={allModelsStatsMatrix}
                colorsByName={brandColors || undefined}
                selectedBrand={firmName}
                keywordsMapper={keywordsMapper}
                commonKeywords={commonKeywords}
                tags={tags}
                valueSuffix="%"
                axisLabelMarkers={axisLabelMarkers}
                renderLegendIcon={renderLegendIcon}
                capitalizeBrands
              />
            </SimpleBar>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareOfVoiceBarChart;
